let audioContext: AudioContext | null = null;

/**
 * Play a short two-note "chime" when a new notification arrives.
 *
 * Uses the Web Audio API (synthesised, no asset/CDN) so it works offline and
 * needs no bundled file. Browsers block audio before any user gesture, so this
 * degrades silently when the AudioContext can't start yet — by the time
 * notifications arrive the user has usually interacted with the page.
 *
 * SSR-safe (no-op on the server).
 */
export function playNotificationSound() {
  if (typeof window === "undefined") return;

  try {
    const Ctx: typeof AudioContext | undefined =
      window.AudioContext || (window as any).webkitAudioContext;

    if (!Ctx) return;

    if (!audioContext) {
      audioContext = new Ctx();
    }

    if (audioContext.state === "suspended") {
      // Best-effort; resolves once the user has interacted with the page.
      void audioContext.resume();
    }

    const ctx = audioContext;
    const now = ctx.currentTime;

    // Pleasant rising two-note chime (G5 → C6).
    const notes = [
      { freq: 784, start: 0, duration: 0.18 },
      { freq: 1047, start: 0.11, duration: 0.24 },
    ];

    for (const { freq, start, duration } of notes) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = "sine";
      osc.frequency.value = freq;

      const t0 = now + start;
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.linearRampToValueAtTime(0.18, t0 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(t0);
      osc.stop(t0 + duration + 0.02);
    }
  } catch {
    // No audio support / autoplay blocked — silently ignore.
  }
}
